import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, Search, X, LayoutGrid, List, ArrowRight, ArrowUpDown } from "lucide-react";
import api from "@/lib/api";
import CourseCard from "@/components/CourseCard";
import AdBanner from "@/components/AdBanner";

const VIEW_KEY = "devpath_courses_view";
const LEVEL_ORDER = { "Einsteiger": 0, "Fortgeschritten": 1 };

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [progress, setProgress] = useState(null);
  const [query, setQuery] = useState("");
  const [view, setView] = useState(localStorage.getItem(VIEW_KEY) || "kacheln");
  const [sortBy, setSortBy] = useState("default");

  const sortCourses = (list) => {
    if (sortBy === "level-asc") {
      return [...list].sort((a, b) => (LEVEL_ORDER[a.level] ?? 99) - (LEVEL_ORDER[b.level] ?? 99));
    }
    if (sortBy === "level-desc") {
      return [...list].sort((a, b) => (LEVEL_ORDER[b.level] ?? 99) - (LEVEL_ORDER[a.level] ?? 99));
    }
    return list;
  };

  useEffect(() => {
    api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    api.get("/progress").then((r) => setProgress(r.data)).catch(() => {});
  }, []);

  const setAndStoreView = (v) => {
    setView(v);
    localStorage.setItem(VIEW_KEY, v);
  };

  const filteredCourses = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((c) =>
      [c.title, c.subtitle, c.description].some((field) => field?.toLowerCase().includes(q))
    );
  }, [courses, query]);

  const groupedByCategory = useMemo(() => {
    const groups = [];
    const indexBySlug = new Map();
    filteredCourses.forEach((c) => {
      const category = c.category || "Sonstiges";
      if (!indexBySlug.has(category)) {
        indexBySlug.set(category, groups.length);
        groups.push({ category, courses: [] });
      }
      groups[indexBySlug.get(category)].courses.push(c);
    });
    return groups;
  }, [filteredCourses]);

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="animate-fade-up">
        <div className="flex items-center gap-2">
          <BookOpen className="h-7 w-7 text-primary" strokeWidth={2.5} />
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">Alle Kurse</h1>
        </div>
        <p className="text-muted-foreground font-medium mt-1">Wähle eine Sprache und leg los.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={2.5} />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Kurse durchsuchen..."
            data-testid="courses-search"
            className="w-full pl-10 pr-9 py-3 border-2 border-black rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-primary"
          />
          {query && (
            <button
              onClick={() => setQuery("")}
              data-testid="courses-search-clear"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-black"
              aria-label="Suche zurücksetzen"
            >
              <X className="h-4 w-4" strokeWidth={2.5} />
            </button>
          )}
        </div>

        <div className="relative shrink-0">
          <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" strokeWidth={2.5} />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            data-testid="courses-sort"
            className="appearance-none pl-9 pr-3 py-3 border-2 border-black rounded-lg font-bold text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary cursor-pointer"
          >
            <option value="default">Standard</option>
            <option value="level-asc">Schwierigkeit: aufsteigend</option>
            <option value="level-desc">Schwierigkeit: absteigend</option>
          </select>
        </div>

        <div className="flex border-2 border-black rounded-lg overflow-hidden shrink-0" data-testid="courses-view-toggle">
          <button
            onClick={() => setAndStoreView("kacheln")}
            data-testid="courses-view-kacheln"
            title="Kachel-Ansicht"
            className={`flex items-center gap-1.5 px-3 py-2.5 font-bold text-sm ${view === "kacheln" ? "bg-black text-white" : "bg-white nb-press"}`}
          >
            <LayoutGrid className="h-4 w-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">Kacheln</span>
          </button>
          <button
            onClick={() => setAndStoreView("liste")}
            data-testid="courses-view-liste"
            title="Listen-Ansicht nach Kategorie"
            className={`flex items-center gap-1.5 px-3 py-2.5 font-bold text-sm border-l-2 border-black ${view === "liste" ? "bg-black text-white" : "bg-white nb-press"}`}
          >
            <List className="h-4 w-4" strokeWidth={2.5} />
            <span className="hidden sm:inline">Liste</span>
          </button>
        </div>
      </div>

      {filteredCourses.length === 0 ? (
        <p className="text-muted-foreground font-medium" data-testid="courses-no-results">
          Keine Kurse gefunden für "{query}".
        </p>
      ) : view === "liste" ? (
        <div className="space-y-8" data-testid="courses-list-view">
          {groupedByCategory.map((group) => (
            <div key={group.category}>
              <h2 className="font-heading font-black text-xl mb-3">{group.category}</h2>
              <div className="nb-card divide-y-2 divide-black overflow-hidden">
                {sortCourses(group.courses).map((c) => {
                  const p = progress?.per_course?.[c.slug];
                  const pct = p?.total ? Math.round((p.done / p.total) * 100) : 0;
                  return (
                    <Link
                      key={c.slug}
                      to={`/kurs/${c.slug}`}
                      data-testid={`course-row-${c.slug}`}
                      className="flex items-center gap-4 px-4 py-3 hover:bg-muted transition-colors"
                    >
                      <span className="h-3 w-3 rounded-full border-2 border-black shrink-0" style={{ backgroundColor: c.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold truncate">{c.title}</div>
                        <div className="text-xs text-muted-foreground font-medium truncate">{c.subtitle}</div>
                      </div>
                      <span className="text-xs font-bold text-muted-foreground shrink-0">{p ? `${p.done}/${p.total} · ${pct}%` : `${c.lesson_count} Lektionen`}</span>
                      <ArrowRight className="h-4 w-4 text-primary shrink-0" strokeWidth={2.5} />
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sortCourses(filteredCourses).map((c) => (
            <CourseCard key={c.slug} course={c} progress={progress?.per_course?.[c.slug]} />
          ))}
          <AdBanner field="courses_snippet" className="nb-card p-4 min-h-[180px] w-full" />
        </div>
      )}
    </div>
  );
}
